import React, { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { contactContent } from '../../mocks/contact-content';

const ContactPage: React.FC = () => {
  const [activeForm, setActiveForm] = useState<string>('general');
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: ''
  });

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>, formType: string) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    let submitUrl = '';
    switch (formType) {
      case 'general':
        submitUrl = 'https://readdy.ai/api/form/d63jl8b0723ekv2itdn0';
        break;
      case 'pilates':
        submitUrl = 'https://readdy.ai/api/form/d63jl8b0723ekv2itdng';
        break;
      case 'thermal':
        submitUrl = 'https://readdy.ai/api/form/d63jl8b0723ekv2itdo0';
        break;
      case 'cafe':
        submitUrl = 'https://readdy.ai/api/form/d63jl8b0723ekv2itdog';
        break;
    }

    try {
      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Thank you. We will be in touch shortly.'
        });
        form.reset();
        setTimeout(() => {
          setFormStatus({ type: null, message: '' });
        }, 5000);
      } else {
        setFormStatus({
          type: 'error',
          message: 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Unable to submit. Please check your connection.'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold uppercase mb-6 text-black" style={{ lineHeight: '1.2' }}>
            {contactContent.headline}
          </h1>
          <p className="text-lg text-reset-text-secondary max-w-2xl mx-auto" style={{ lineHeight: '1.6' }}>
            {contactContent.subheadline}
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 px-6 bg-reset-bg-alt">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12">
            {contactContent.contactInfo.map((info, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className={`${info.icon} text-3xl text-reset-sage`}></i>
                </div>
                <h3 className="text-sm uppercase font-bold mb-3 text-black tracking-wider">
                  {info.title}
                </h3>
                <div className="text-reset-text-secondary" style={{ lineHeight: '1.6' }}>
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="mb-1">{detail}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Selection Tabs */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {contactContent.formTypes.map((formType) => (
              <button
                key={formType.id}
                onClick={() => {
                  setActiveForm(formType.id);
                  setFormStatus({ type: null, message: '' });
                }}
                className={`px-6 py-3.5 min-h-[44px] rounded-lg text-base uppercase font-semibold tracking-wider transition-all duration-200 ease-in-out active:scale-[0.98] whitespace-nowrap cursor-pointer ${
                  activeForm === formType.id
                    ? 'bg-[#7F9C8B] text-white shadow-[0_2px_8px_rgba(127,156,139,0.25)]'
                    : 'bg-[#DDE6E1] text-[#7F9C8B] hover:bg-[#7F9C8B] hover:text-white hover:shadow-[0_2px_8px_rgba(127,156,139,0.25)]'
                }`}
              >
                {formType.label}
              </button>
            ))}
          </div>

          {/* Form Status Message */}
          {formStatus.type && (
            <div
              className={`mb-8 p-4 rounded-lg text-center ${
                formStatus.type === 'success'
                  ? 'bg-reset-sage-light text-reset-sage'
                  : 'bg-red-50 text-red-600'
              }`}
            >
              {formStatus.message}
            </div>
          )}

          {/* General Enquiry Form */}
          {activeForm === 'general' && (
            <form
              id="general-enquiry-form"
              data-readdy-form
              onSubmit={(e) => handleFormSubmit(e, 'general')}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold uppercase mb-3 text-black">General Enquiry</h2>
                <p className="text-reset-text-secondary">Ask us anything about RE:SET</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="general-first-name" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="general-first-name"
                    name="first_name"
                    required
                    className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="general-last-name" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="general-last-name"
                    name="last_name"
                    required
                    className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="general-email" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="general-email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                />
              </div>

              <div>
                <label htmlFor="general-phone" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="general-phone"
                  name="phone"
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                />
              </div>

              <div>
                <label htmlFor="general-message" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Your Message *
                </label>
                <textarea
                  id="general-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm resize-none"
                  placeholder="Tell us how we can help..."
                ></textarea>
                <p className="text-xs text-reset-text-secondary mt-2">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                className="w-full bg-[#7F9C8B] text-white px-8 py-3.5 min-h-[44px] rounded-lg text-base uppercase font-semibold tracking-wider shadow-[0_2px_8px_rgba(127,156,139,0.25)] hover:bg-[#5E7568] hover:shadow-[0_4px_12px_rgba(127,156,139,0.35)] transition-all duration-200 ease-in-out active:scale-[0.98] whitespace-nowrap cursor-pointer"
              >
                Send Enquiry
              </button>
            </form>
          )}

          {/* Pilates Booking Form */}
          {activeForm === 'pilates' && (
            <form
              id="pilates-booking-form"
              data-readdy-form
              onSubmit={(e) => handleFormSubmit(e, 'pilates')}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold uppercase mb-3 text-black">Reformer Pilates Booking</h2>
                <p className="text-reset-text-secondary">Reserve your spot in our boutique studio</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="pilates-first-name" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="pilates-first-name"
                    name="first_name"
                    required
                    className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="pilates-last-name" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="pilates-last-name"
                    name="last_name"
                    required
                    className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="pilates-email" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="pilates-email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                />
              </div>

              <div>
                <label htmlFor="pilates-phone" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="pilates-phone"
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                />
              </div>

              <div>
                <label htmlFor="pilates-membership" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Membership Status *
                </label>
                <select
                  id="pilates-membership"
                  name="membership_status"
                  required
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm cursor-pointer"
                >
                  <option value="">Select your status</option>
                  <option value="Current Member">Current Member</option>
                  <option value="New to RE:SET">New to RE:SET</option>
                  <option value="Interested in Membership">Interested in Membership</option>
                </select>
              </div>

              <div>
                <label htmlFor="pilates-experience" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Pilates Experience *
                </label>
                <select
                  id="pilates-experience"
                  name="experience_level"
                  required
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm cursor-pointer"
                >
                  <option value="">Select your experience</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>

              <div>
                <label htmlFor="pilates-notes" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Additional Notes
                </label>
                <textarea
                  id="pilates-notes"
                  name="notes"
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm resize-none"
                  placeholder="Any injuries, preferences, or questions..."
                ></textarea>
                <p className="text-xs text-reset-text-secondary mt-2">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                className="w-full bg-[#7F9C8B] text-white px-8 py-3.5 min-h-[44px] rounded-lg text-base uppercase font-semibold tracking-wider shadow-[0_2px_8px_rgba(127,156,139,0.25)] hover:bg-[#5E7568] hover:shadow-[0_4px_12px_rgba(127,156,139,0.35)] transition-all duration-200 ease-in-out active:scale-[0.98] whitespace-nowrap cursor-pointer"
              >
                Request Booking
              </button>
            </form>
          )}

          {/* Thermal Suite Booking Form */}
          {activeForm === 'thermal' && (
            <form
              id="thermal-booking-form"
              data-readdy-form
              onSubmit={(e) => handleFormSubmit(e, 'thermal')}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold uppercase mb-3 text-black">Thermal Suite Booking</h2>
                <p className="text-reset-text-secondary">Reserve your private restoration session</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="thermal-first-name" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="thermal-first-name"
                    name="first_name"
                    required
                    className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="thermal-last-name" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="thermal-last-name"
                    name="last_name"
                    required
                    className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="thermal-email" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="thermal-email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                />
              </div>

              <div>
                <label htmlFor="thermal-phone" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="thermal-phone"
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                />
              </div>

              <div>
                <label htmlFor="thermal-guests" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Number of Guests *
                </label>
                <select
                  id="thermal-guests"
                  name="number_of_guests"
                  required
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm cursor-pointer"
                >
                  <option value="">Select number of guests</option>
                  <option value="1">Solo Session</option>
                  <option value="2">Two Guests</option>
                </select>
              </div>

              <div>
                <label htmlFor="thermal-date" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Preferred Date *
                </label>
                <input
                  type="date"
                  id="thermal-date"
                  name="preferred_date"
                  required
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm cursor-pointer"
                />
              </div>

              <div>
                <label htmlFor="thermal-time" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Preferred Time *
                </label>
                <select
                  id="thermal-time"
                  name="preferred_time"
                  required
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm cursor-pointer"
                >
                  <option value="">Select preferred time</option>
                  <option value="Morning (8am-12pm)">Morning (8am-12pm)</option>
                  <option value="Afternoon (12pm-5pm)">Afternoon (12pm-5pm)</option>
                  <option value="Evening (5pm-9pm)">Evening (5pm-9pm)</option>
                </select>
              </div>

              <div>
                <label htmlFor="thermal-notes" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Additional Notes
                </label>
                <textarea
                  id="thermal-notes"
                  name="notes"
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm resize-none"
                  placeholder="Any special requests or health considerations..."
                ></textarea>
                <p className="text-xs text-reset-text-secondary mt-2">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                className="w-full bg-[#7F9C8B] text-white px-8 py-3.5 min-h-[44px] rounded-lg text-base uppercase font-semibold tracking-wider shadow-[0_2px_8px_rgba(127,156,139,0.25)] hover:bg-[#5E7568] hover:shadow-[0_4px_12px_rgba(127,156,139,0.35)] transition-all duration-200 ease-in-out active:scale-[0.98] whitespace-nowrap cursor-pointer"
              >
                Request Booking
              </button>
            </form>
          )}

          {/* Café Reservation Form */}
          {activeForm === 'cafe' && (
            <form
              id="cafe-reservation-form"
              data-readdy-form
              onSubmit={(e) => handleFormSubmit(e, 'cafe')}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold uppercase mb-3 text-black">Café Reservation</h2>
                <p className="text-reset-text-secondary">Reserve a table for your visit</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="cafe-first-name" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="cafe-first-name"
                    name="first_name"
                    required
                    className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="cafe-last-name" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="cafe-last-name"
                    name="last_name"
                    required
                    className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="cafe-email" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="cafe-email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                />
              </div>

              <div>
                <label htmlFor="cafe-phone" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="cafe-phone"
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="cafe-date" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                    Reservation Date *
                  </label>
                  <input
                    type="date"
                    id="cafe-date"
                    name="reservation_date"
                    required
                    className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm cursor-pointer"
                  />
                </div>
                <div>
                  <label htmlFor="cafe-time" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                    Reservation Time *
                  </label>
                  <select
                    id="cafe-time"
                    name="reservation_time"
                    required
                    className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm cursor-pointer"
                  >
                    <option value="">Select time</option>
                    <option value="8:00 AM">8:00 AM</option>
                    <option value="9:00 AM">9:00 AM</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="12:00 PM">12:00 PM</option>
                    <option value="1:00 PM">1:00 PM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                    <option value="5:00 PM">5:00 PM</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="cafe-party-size" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Party Size *
                </label>
                <select
                  id="cafe-party-size"
                  name="party_size"
                  required
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm cursor-pointer"
                >
                  <option value="">Select party size</option>
                  <option value="1">1 Guest</option>
                  <option value="2">2 Guests</option>
                  <option value="3">3 Guests</option>
                  <option value="4">4 Guests</option>
                  <option value="5+">5+ Guests</option>
                </select>
              </div>

              <div>
                <label htmlFor="cafe-notes" className="block text-sm uppercase font-bold mb-2 text-black tracking-wider">
                  Special Requests
                </label>
                <textarea
                  id="cafe-notes"
                  name="special_requests"
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-reset-border rounded-lg focus:outline-none focus:border-reset-sage transition-colors text-sm resize-none"
                  placeholder="Dietary requirements, seating preferences..."
                ></textarea>
                <p className="text-xs text-reset-text-secondary mt-2">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                className="w-full bg-[#7F9C8B] text-white px-8 py-3.5 min-h-[44px] rounded-lg text-base uppercase font-semibold tracking-wider shadow-[0_2px_8px_rgba(127,156,139,0.25)] hover:bg-[#5E7568] hover:shadow-[0_4px_12px_rgba(127,156,139,0.35)] transition-all duration-200 ease-in-out active:scale-[0.98] whitespace-nowrap cursor-pointer"
              >
                Request Reservation
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 px-6 bg-reset-blue-light">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold uppercase mb-4 text-black">Visit Us</h2>
            <p className="text-reset-text-secondary max-w-2xl mx-auto" style={{ lineHeight: '1.6' }}>
              {contactContent.visitInfo.description}
            </p>
          </div>

          <div className="bg-white rounded-lg overflow-hidden shadow-sm">
            <iframe
              src={contactContent.visitInfo.mapEmbed}
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="RE:SET Location Map"
            ></iframe>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block text-left">
              <p className="text-lg font-bold text-black mb-2">{contactContent.visitInfo.address}</p>
              <p className="text-reset-text-secondary mb-1">{contactContent.visitInfo.street}</p>
              <p className="text-reset-text-secondary mb-6">{contactContent.visitInfo.city}</p>
              
              <div className="space-y-4">
                {contactContent.visitInfo.transport.map((transportInfo, idx) => (
                  <div key={idx}>
                    <div className="flex items-center gap-2 mb-2">
                      <i className={`${transportInfo.icon} text-reset-sage`}></i>
                      <span className="text-sm font-bold text-black uppercase tracking-wide">
                        {transportInfo.icon.includes('train') ? 'Train' : transportInfo.icon.includes('bus') ? 'Bus' : 'PARKING'}
                      </span>
                    </div>
                    {transportInfo.details.map((detail, didx) => (
                      <p key={didx} className="text-sm text-reset-text-secondary ml-6">
                        {detail}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
